/**
 * Pure decision logic for `/auth/refresh` outcomes, shared by the axios
 * interceptor (lib/api.js) and the Socket.IO handshake recovery
 * (lib/socket.js).
 *
 * Kept free of axios / socket.io-client / `import.meta.env` so it runs
 * under `node --test` (tests/session-refresh.test.js).
 *
 * A refresh result is always `{ ok, terminalError, noSession }`:
 *   - `ok: true`          → fresh access cookie is on the browser; retry.
 *   - `terminalError`     → the session is dead; force a logout.
 *   - `noSession: true`   → the server reported `{ session: false }`: this
 *                           browser holds NO session cookies at all. There
 *                           is nothing to refresh and retrying cannot help.
 *   - all falsy           → transient (network blip, rate limit); keep the
 *                           user signed in and try again later.
 */

/** Error codes that mean "this session is over — kick the user out".
 *  Set by the auth middleware on 401/403 when the user/tenant has been
 *  deleted, deactivated, suspended, or had its password changed
 *  elsewhere. The SPA reacts by clearing local state and showing a
 *  toast on the sign-in page.
 *
 *  `session_expired` covers the cases where /auth/refresh can't recover
 *  the session — refresh cookie missing, refresh-token row revoked /
 *  expired / wiped by a tenant-delete cascade — and the SPA must hard-
 *  logout rather than keep the chrome up with 401-ing data fetches. */
export const TERMINAL_LOGOUT_CODES = new Set([
  'user_deleted',
  'user_deactivated',
  'tenant_deleted',
  'tenant_suspended',
  'password_changed',
  'session_expired',
  // §3 IP access controls. `disallowed_country` is raised by the geo
  // blocker inside jwtAuth (plan region-locked to its purchase country);
  // `ip_blocked` by the pre-auth manual deny/allow list. Both are
  // unrecoverable for the current session, so we force a hard logout.
  'disallowed_country',
  'ip_blocked',
])

/** Shape a SUCCESSFUL (2xx) `/auth/refresh` response body. */
export function classifyRefreshResponse(data) {
  // Backend returns `200 { session: false }` when neither cookie is
  // present (a fresh visitor on the sign-in page, or someone who has
  // already been signed out). That's NOT a terminal failure — but it is
  // not transient either: no amount of retrying will produce a session.
  // Callers must stop (socket) or treat it as a signed-out browser.
  if (data?.session === false) {
    return { ok: false, terminalError: null, noSession: true }
  }
  return { ok: true, terminalError: null, noSession: false }
}

/** Shape a FAILED `/auth/refresh` call (axios-style error). */
export function classifyRefreshError(err) {
  // Surface terminal error codes from /auth/refresh so the SPA can
  // distinguish "session expired" from "your account was deleted".
  const code = err?.response?.data?.error?.code
  const message = err?.response?.data?.error?.message
  if (TERMINAL_LOGOUT_CODES.has(code)) {
    return { ok: false, terminalError: { code, message }, noSession: false }
  }
  // [QA-03] Rate-limited (429). The refresh limiter is keyed per IP, so a
  // 429 says nothing about THIS session — it means "too many refreshes
  // from this network". Treating it as a dead session force-logged-out
  // every signed-in user behind a shared office/NAT IP. It is transient.
  if (err?.response?.status === 429) {
    return { ok: false, terminalError: null, noSession: false }
  }
  // Any other refresh failure that came back with an HTTP response
  // (e.g. an older deploy that returns a plain `unauthorized` code,
  // or a 4xx the server adds later) means the session is genuinely
  // dead — synthesise a terminal error so the interceptor force-
  // logs-out instead of leaving the SPA chrome up while every data
  // fetch 401s. Crucially this catches the tenant-cascade case:
  // when Super Admin deletes a company, every member's refresh
  // token row is wiped and the next refresh attempt fails. Without
  // this fallback, members who weren't socket-connected at the
  // moment of deletion would see "Authentication required" stuck
  // in the page instead of being redirected to sign-in.
  if (err?.response) {
    // Deliberately NOT `message ?? …`. This branch is the catch-all for
    // a refresh that failed WITHOUT a recognised terminal code, which in
    // practice means the plain 401 the auth middleware returns for a
    // missing or expired cookie — whose message is "Authentication
    // required." That states a fact and gives the user nothing to act
    // on, and it was being shown verbatim when a session lapsed
    // mid-task. Responses that DO carry a meaningful terminal code
    // (account deleted, company removed, suspended) are handled by the
    // `TERMINAL_LOGOUT_CODES` branch above and keep their own wording.
    return {
      ok: false,
      terminalError: {
        code: code ?? 'session_expired',
        message: 'Your session has expired. Please sign in again.',
      },
      noSession: false,
    }
  }
  // True network error (no response): bubble up without forcing a
  // logout so a brief wifi flicker doesn't punt the user back to
  // the sign-in page mid-task.
  return { ok: false, terminalError: null, noSession: false }
}

/**
 * What the Socket.IO `connect_error` (unauthorized) handler should do with
 * a refresh result:
 *   - 'reconnect' → fresh cookie; open a new handshake now.
 *   - 'logout'    → session is dead; force-logout, never reconnect.
 *   - 'stop'      → [QA-03] no session cookies at all (signed out). Never
 *                   retry: each retry is another /auth/refresh POST, and a
 *                   signed-out tab looping every 3 s exhausted the per-IP
 *                   refresh limit for every signed-in user on that network.
 *   - 'retry'     → transient; try again after a backoff delay.
 */
export function socketRecoveryAction(result) {
  if (result?.ok) return 'reconnect'
  if (result?.terminalError) return 'logout'
  if (result?.noSession) return 'stop'
  return 'retry'
}

/** Exponential backoff for transient socket-auth recovery: 3 s, 6 s,
 *  12 s, 24 s, 48 s, then capped at 60 s. `attempt` is 0-based. */
export function reconnectDelayMs(attempt) {
  const n = Math.max(0, Math.floor(Number(attempt) || 0))
  return Math.min(60_000, 3_000 * 2 ** Math.min(n, 5))
}
